import { useEffect } from 'react';
import { CloudinaryProductAssetGroup } from 'shared/types/content';
import { Variant } from 'shared/types/product';
import { preload } from 'swr';
import { sdk } from 'sdk';
import { LEAD_ATTRIBUTE_KEYS } from '../helpers/getVariantSpecies';
import { getAttributeValue, getImageCacheKey } from '../helpers/imageCacheKey';

/**
 * Warms the SWR cache with the image the shopper would land on for each other value of the lead
 * axis (species, then material), after the initial paint.
 *
 * Only the lead axis is prefetched, deliberately. `useProductBaseImages` keys on every axis the
 * image varies on, so preloading one request per distinct key would mean 18 on the Angle Tension
 * Post and grows with the catalog — a burst large enough to hit the request limiter, which then
 * sheds most of it. The lead axis is the one worth the spend: it is the identity choice shoppers
 * browse visually and flip between, while height, post location or turn direction are configuration
 * steps that can afford one on-demand fetch.
 *
 * Each prefetch targets the variant the shopper would actually land on — the one matching the
 * current variant on every *other* axis — so the warmed key is the one that gets read, not merely
 * some variant of that lead value. Uses the same `[action, productId, imageKey]` key shape as
 * `useProductBaseImages` so its results populate that hook's cache. The current lead value is
 * skipped: `useProductBaseImages` already fetches it on mount (`revalidateOnMount`), and SWR's
 * `preload` would not dedupe against that hook fetch.
 */
export const usePrefetchLeadAxisImages = (
  productId: string | undefined,
  variants: Variant[] | undefined,
  currentVariant: Variant | undefined,
) => {
  useEffect(() => {
    if (!productId || !variants?.length || !currentVariant) return;

    const leadKey = LEAD_ATTRIBUTE_KEYS.find((key) => getAttributeValue(currentVariant, key) !== undefined);
    if (!leadKey) return;

    // Everything about a configuration *except* the lead axis, so we can spot the variant a shopper
    // would land on if they changed only that axis. Compared through the key rather than raw values
    // because enum attributes arrive as `{ key, label }` objects that are never `===` each other.
    const otherAxesOf = (variant: Variant) => getImageCacheKey(variant, [leadKey]);
    const currentLeadValue = getAttributeValue(currentVariant, leadKey);
    const currentOtherAxes = otherAxesOf(currentVariant);

    const seenLeadValues = new Set<string>();
    for (const variant of variants) {
      const leadValue = getAttributeValue(variant, leadKey);
      if (!leadValue || leadValue === currentLeadValue || seenLeadValues.has(leadValue)) continue;

      // The exact configuration the shopper would land on, falling back to any variant of this lead
      // value when this product does not offer the current configuration in it.
      const representative =
        variants.find(
          (candidate) =>
            getAttributeValue(candidate, leadKey) === leadValue && otherAxesOf(candidate) === currentOtherAxes,
        ) ?? variant;

      seenLeadValues.add(leadValue);

      // `sdk.callAction` resolves `{isError: true}` instead of rejecting, so a request the limiter
      // sheds would otherwise be preloaded as a *successful* empty result: the shopper switches to
      // that material and reads a cached error, losing the Cloudinary gallery until SWR revalidates
      // the key. Throwing keeps the failure out of the cache so the read fetches for real.
      const request = preload(
        ['action/assets/getProductAssetsBySku', productId, getImageCacheKey(representative)],
        async () => {
          const response = await sdk.callAction<CloudinaryProductAssetGroup>({
            actionName: 'assets/getProductAssetsBySku',
            query: { sku: representative.sku },
          });
          if (response.isError) throw response;
          return response;
        },
      );
      // A prefetch for a material the shopper never selects is never consumed, and SWR keeps the
      // promise in its PRELOAD map either way — so handle the rejection here to keep a shed request
      // from surfacing as an unhandled rejection.
      request.catch(() => undefined);
    }
  }, [productId, variants, currentVariant]);
};
